<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" max-width="1100px" :fullscreen="fullscreen" :content-class="dialog_class" persistent scrollable>
<k-card>
	<k-card-title class="k-rcram-title"
		:style="`background-color:${report_banner_bgd_color}`"
		v-if="!opened_from_embedded"
	>
		<!-- Close btn is first in the DOM so it is the first control to receive focus when the dialog opens; order:1 keeps it visually last -->
		<k-btn color="#666" dark fab small accessible-label="Close resource alignment/crosswalk tool" style="order:1"
			v-if="!opened_from_embedded"
			@click="close_interface"
		><k-icon>fas fa-arrow-left</k-icon></k-btn>

		<img class="k-rcram-logo" 
			:style="report_logo_style"
			:src="report_logo"
		>
		<div class="k-rcram-report-title"
			v-html="report_title"
		></div>

		<k-spacer/>

		<v-tooltip bottom><template v-slot:activator="{on}">
			<k-btn v-on="on" fab small color="#fff" class="mr-5"
				@click="delete_job"
			><k-icon color="red darken-4">fas fa-trash-alt</k-icon></k-btn>
		</template>Delete this Job</v-tooltip>

		<v-tooltip bottom><template v-slot:activator="{on}">
			<k-btn v-on="on" fab small color="#fff" class="mr-12"
				@click="clone_job"
			><k-icon color="primary">fas fa-clone</k-icon></k-btn>
		</template>Clone this Job</v-tooltip>

	</k-card-title>
	<k-card-text class="k-scram-job-card" style="font-size:14px; line-height:17px"
		v-if='initialized'
	>

	<!-- ============================================= -->
	<!-- Job ID, title, and report_text -->
		<div class="mt-5 d-flex align-start" style="font-size:18px;"
		>
			<div class="mr-5 mt-2"
				v-if="cram_job_id"
			>
				<b>Job ID:</b> {{cram_job_id}}
			</div>

			<div class="mr-2 mt-2">
				<b>Title:</b>
			</div>

			<div style="width:700px">
				<k-textarea outlined dense hide-details placeholder="" rows="1" auto-grow
					aria-label="Description"
					v-model="cram_job_title"
				></k-textarea>

				<k-textarea outlined xdense hide-details placeholder="" rows="1" auto-grow class="mt-2"
					v-if="report_text || show_empty_report_text_interface"
					aria-label="Report Text"
					label="Report Text"
					v-model="report_text"
				></k-textarea>

			</div>

			<div>
				<v-tooltip bottom><template v-slot:activator="{on}">
					<k-btn v-on="on" fab outlined x-small color="#333" dark class="ml-4 mt-1"
						v-show="!report_text"
						@click="show_empty_report_text_interface=!show_empty_report_text_interface"
					><k-icon>fas fa-flip-vertical fa-align-left</k-icon></k-btn>
				</template>Enter Report Text</v-tooltip>
			</div>

			<k-spacer />

			<div v-if="cram_job_id" class="mt-1 k-rcram-job-type-indicator">
				{{(job_type == 'align') ? 'ALIGNMENT' : 'CROSSWALK'}}
			</div>

			<div v-else class="mt-2">
				<b class="mr-2">Job Type:</b>
				<k-btn-toggle dense active-class="k-toggle-btn-active-class"
					class="k-toggle-btn" v-model="job_type" mandatory>
					<k-btn small light value="align">Alignment</k-btn>
					<k-btn small light value="crosswalk">Crosswalk</k-btn>
				</k-btn-toggle>
			</div>

		</div>

		<div :class="!show_universe_details ? 'd-flex justify-center mt-4' : (!universes_specified ? 'mt-5' : '')">

		<!-- ============================================= -->
		<!-- Resource Universe -->
			<div class="pt-3">
				<h3 class="mr-3">
					<nobr>{{ru_label}} Universe:</nobr>
				</h3>

				<div class="d-flex align-start mt-3">
					<div class="ml-4 mr-2"
						v-if="show_universe_details"
					>
						<FrameworkSwitcher2 color="primary" 
							:color="ru.frameworkIdentifier ? '#fff' : 'primary'" 
							:bstyle="ru.frameworkIdentifier ? 'background-color:#000!important; border:1px solid #000!important' : ''"
							:btn_size="ru.frameworkIdentifier ? 'small' : 'med'"
							:current_selected_framework_identifier="ru.frameworkIdentifier" 
							:large_btn_content="resource_framework_switcher_btn_content"
							:framework_record_list="resource_framework_switcher_framework_records"
							@framework_selected="set_resource_framework" 
							v-if="job_type_switch_complete"
						/>
					</div>

					<div class="k-scram-job-framework-title mr-4"
						v-if="ru.frameworkIdentifier"
						v-html="framework_title_display(ru_fr)"
					></div>

					<k-btn small color="#000" dark class="mr-2 k-tight-btn k-nocaps-btn"
						v-if="show_universe_details && ru.frameworkIdentifier"
						@click="choose_resource_branches"
					><k-icon small>fas fa-code-branch</k-icon><span v-if="ru.itemIdentifiers.length==0" class="ml-2">Choose Branches</span></k-btn>
					
					<div class="k-scram-job-branch-title mr-2"
						v-if="ru.itemIdentifiers[0]"
						v-html="branch_title_display(ru_fr, ru.itemIdentifiers)"
					></div>

					<div style="width:240px"
						v-if="show_universe_details && ru.frameworkIdentifier"
					>
						<k-select background-color="#fff" outlined densex hide-details
							multiple
							:label="`${ru_label} Types`"
							v-model="ru.CFItemTypes" 
							:items="crosswalk_job ? cu_item_type_list(ru_fr) : item_type_list(ru_fr)"
							:menu-props="{ top: true, dense: true }"
							v-if="job_type_switch_complete"
						></k-select>
					</div>

					<!-- <k-spacer/>

					<k-text-field class="k-scram-job-guid-field mr-2" outlined dense hide-details background-color="#fff"
						v-if="show_identifier_text_fields"
						label="Framework GUID" 
						v-model="ru.frameworkIdentifier"
					></k-text-field>

					<k-text-field class="k-scram-job-guid-field mr-0" outlined dense hide-details background-color="#fff"
						v-if="show_identifier_text_fields"
						label="Branch GUID" 
						v-model="ru.itemIdentifiers[0]"
					></k-text-field> -->
				</div>

				<div class="d-flex align-center ml-4 mt-3"
					v-if="crosswalk_job && show_universe_details && ru.frameworkIdentifier"
				>
					<div>{{ru_label}} education levels:</div>

					<div class="ml-2" style="width:200px">
						<k-select background-color="#fff" outlined dense hide-details
							multiple
							v-model="ru.educationLevel" 
							:items="education_level_list()"
							:menu-props="{ top: true, dense: true }"
						></k-select>
					</div>
				</div>

				<div class="d-flex align-center ml-4 mt-3"
					v-if="align_job && show_universe_details && ru.frameworkIdentifier && aggregation_item_type_list().length > 2"
				>
					<div>Item type to evaluate via aggregation:</div>

					<div class="ml-2" style="width:240px">
						<k-select background-color="#fff" outlined dense hide-details
							v-model="payload.resourceAggregationItemType" 
							:items="aggregation_item_type_list()"
							:menu-props="{ top: true, dense: true }"
						></k-select>
					</div>
				</div>
			</div>

		<!-- ============================================= -->
		<!-- Competency Universe -->
			<div
				:class="show_universe_details ? 'mt-5 mb-5' : 'pt-3 ml-6 pl-6 pb-2'"
				:style="show_universe_details ? '' : 'border-left:3px solid #000;'"
			>
				<h3><nobr>{{cu_label}} Universe:</nobr></h3>

				<div class="d-flex align-start mt-3"
					v-for="(cu, cui) in competency_universe_items"
					:key="cu.frameworkIdentifier"
				>
					<div class="ml-4 mr-2"
						v-if="show_universe_details"
					>
						<FrameworkSwitcher2 
							:color="cu.frameworkIdentifier ? '#fff' : 'primary'" 
							:bstyle="cu.frameworkIdentifier ? 'background-color:#000!important; border:1px solid #000!important' : ''"
							:btn_size="cu.frameworkIdentifier ? 'small' : 'med'"
							:current_selected_framework_identifier="cu.frameworkIdentifier" 
							:large_btn_content="comp_framework_switcher_btn_content"
							:framework_record_list="comp_framework_switcher_framework_records"
							@framework_selected="set_comp_framework(cui, $event)" 
						/>
					</div>

					<div class="k-scram-job-framework-title mr-2"
						v-if="cu.frameworkIdentifier"
						v-html="framework_title_display(cu_fr[cui])"
					></div>
					
					<k-btn small color="#000" dark class="mr-2 k-tight-btn k-nocaps-btn"
						v-if="show_universe_details && cu.frameworkIdentifier"
						@click="choose_comp_branches(cui)"
					><k-icon small>fas fa-code-branch</k-icon><span v-if="cu.itemIdentifiers.length==0" class="ml-2">Choose Branches</span></k-btn>

					<div class="k-scram-job-branch-title mr-2"
						v-if="cu.itemIdentifiers[0]"
						v-html="branch_title_display(cu_fr[cui], cu.itemIdentifiers)"
					></div>

					<div style="width:240px"
						v-if="show_universe_details && cu.frameworkIdentifier"
					>
						<k-select background-color="#fff" outlined densex hide-details
							multiple
							:label="`${cu_label} Types`"
							v-model="cu.CFItemTypes" 
							@change="cu_item_types_changed(cu)"
							:items="cu_item_type_list(cu_fr[cui])"
							:menu-props="{ top: true, dense: true }"
						></k-select>
					</div>

					<!-- <k-spacer/>

					<k-text-field class="k-scram-job-guid-field mr-2" outlined dense hide-details background-color="#fff"
						v-if="show_identifier_text_fields"
						label="Framework GUID" 
						v-model="cu.frameworkIdentifier"
					></k-text-field>

					<k-text-field class="k-scram-job-guid-field mr-0" outlined dense hide-details background-color="#fff"
						v-if="show_identifier_text_fields"
						label="Branch GUID" 
						v-model="cu.itemIdentifiers[0]"
					></k-text-field> -->
				</div>

				<!-- to use this interface we require all CUs to have the same educationLevel values (at least for now) -->
				<div class="d-flex align-center ml-4 mt-3"
					v-if="show_universe_details && competency_universe_items[0].frameworkIdentifier"
				>
					<div>{{cu_label}} education levels:</div>

					<div class="ml-2" style="width:200px">
						<k-select background-color="#fff" outlined dense hide-details
							multiple
							v-model="competency_universe_items[0].educationLevel" 
							:items="education_level_list()"
							:menu-props="{ top: true, dense: true }"
						></k-select>
					</div>

					<div class="ml-5">Education level (EL) match tolerance:</div>

					<div class="ml-2" style="width:320px">
						<k-select background-color="#fff" outlined dense hide-details
							v-model="payload.educationLevelMatchTolerance" 
							:items="education_level_tolerance_list()"
							:menu-props="{ top: true, dense: true }"
						></k-select>
					</div>
				</div>
			</div>
		</div>

		<div class="text-center">
			<k-btn color="primary"
				v-show="universes_specified && show_universe_details"
				@click="show_universe_details_toggle = false"
			><k-icon class="mr-2">fas fa-caret-up</k-icon>Hide Universe Details</k-btn>

			<k-btn color="primary"
				v-show="!show_universe_details" class="text-center mt-5"
				@click="show_universe_details_toggle = true"
			><k-icon class="mr-2">fas fa-caret-down</k-icon>Show/Edit Universe Details</k-btn>
		</div>

	<!-- ============================================= -->
	<!-- Controls for using this SCRAM job -->

		<!-- Save btn, or indication that nothing needs to be saved -->
		<div class="mt-5 d-flex justify-center" 
			style="border-top:1px solid #999; position:relative"
		>
			<k-btn color="primary" class="mx-2 mt-3"
				v-if="!job_status && save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-save</k-icon> Save Job</k-btn>

			<k-btn color="green darken-3" dark class="mx-2 pt-3 elevation-0"
				v-if="!job_status && !save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-circle-check</k-icon> Job Saved</k-btn>

			<!-- Hidden btn to call evaluate_params; this should be done automatically when needed -->
			<k-btn small style="opacity:0; position:absolute; right:0; top:0;"
				@click="evaluate_params"
			>evaluate_params</k-btn>

			<!-- <k-btn color="primary"
				@click="evaluate_params"
			>Evaluate SCRAM Job <k-icon smallX class="ml-2">fas fa-arrow-right</k-icon></k-btn> -->
		</div>

		<!-- -------------------------------------------- -->
		<!-- Matrix stats / status / processing controls -->
		<div class="k-scram-job-status mt-5"
			v-if="!worker_status_includes(['unknown'])"
		>
			<div class="mx-auto" style="width:1000px">
				<SCRAMJobStats 
					:job_status_prop="job_status" 
					:matrix_prop="matrix"
					@edit_resources="edit_resources"
					@edit_comps="edit_comps"
				/>
			</div>

			<!-- -------------------------------------------- -->
			<!-- alignment matrix progress -->
			<div class="d-flex justify-center mt-6"
				v-if="align_job && worker_needs_to_run && !worker_status_includes(['processing'])"
			>
				<div class="ml-0">Matrix cells to calculate: <b>{{matrix_stats.missingGenAICount}}</b></div>
			</div>

			<div class="d-flex justify-center align-center mt-3"
				v-if="align_job && !worker_needs_to_run"
			>
				<div class="ml-0">
					<k-icon color="green darken-3" class="mr-1">fas fa-circle-check</k-icon>
					All Matrix cells ({{matrix_stats.matrixCellCount}}) calculated
				</div>
			</div>

			<!-- progress bar for running genai -->
			<div class="d-flex justify-center align-center mt-3" style="font-size:16px;"
				v-if="align_job && worker_needs_to_run"
			>
				<div class="mr-2 text-right" style="width:312px;">Matrix cells processed: <b>{{cells_processed}}</b></div>
				<div style="width:400px">
					<k-progress-linear height="32" color="green" rounded
						:stream="worker_status_includes(['processing', 'cancel_requested'])"
						:buffer-value="worker_status_includes(['processing', 'cancel_requested']) ? 0 : 100"
						v-model="cells_processed_pct" 
					>
						<template v-slot:default="{ value }">
							<strong>{{ Math.ceil(value) }}%</strong>
						</template>
					</k-progress-linear>
				</div>
				<div class="ml-2"><b>{{matrix_stats.matrixCellCount}}</b></div>
			</div>

			<!-- -------------------------------------------- -->
			<!-- crosswalk progress -->
			<div v-if="matrix_stats.crosswalkCount > 0">
				<div class="d-flex justify-center mt-6"
					v-if="worker_needs_to_run && !worker_status_includes(['processing'])"
				>
					<div class="ml-0">Crosswalk associations to calculate: <b>{{matrix_stats.missingCrosswalkCount}}</b> calculated ({{matrix_stats.crossAlignmentCount}} cross-alignments)</div>
				</div>

				<div class="d-flex justify-center align-center mt-3"
					v-if="!worker_needs_to_run"
				>
					<div class="ml-0">
						<k-icon color="green darken-3" class="mr-1">fas fa-circle-check</k-icon>
						All Crosswalk associations ({{matrix_stats.crosswalkCount}}) calculated ({{matrix_stats.crossAlignmentCount}} cross-alignments)
					</div>
				</div>

				<!-- progress bar for running genai for crosswalk associations -->
				<div class="d-flex justify-center align-center mt-3" style="font-size:16px;"
					v-if="worker_needs_to_run"
				>
					<div class="mr-2 text-right" style="width:312px;">Crosswalk associations processed: <b>{{crosswalks_processed}}</b></div>
					<div style="width:400px">
						<k-progress-linear height="32" color="green" rounded
							:stream="worker_status_includes(['processing', 'cancel_requested'])"
							:buffer-value="worker_status_includes(['processing', 'cancel_requested']) ? 0 : 100"
							v-model="crosswalks_processed_pct" 
						>
							<template v-slot:default="{ value }">
								<strong>{{ Math.ceil(value) }}%</strong>
							</template>
						</k-progress-linear>
					</div>
					<div class="ml-2"><b>{{matrix_stats.crosswalkCount}}</b></div>
				</div>
			</div>

			<!-- -------------------------------------------- -->
			<!-- buttons for controlling genai processing -->
			<div class="d-flex justify-center align-center mt-5"
				v-if="worker_needs_to_run"
			>
				<!-- <k-btn color="secondary" class="mx-2 k-tight-btn k-nocaps-btn"
					v-if="!worker_status_includes(['queued', 'processing']) && worker_needs_to_run"
					@click="evaluate_params"
				><k-icon small class="mr-2">fas fa-arrows-rotate</k-icon>Re-Evaluate Parameters</k-btn> -->

				<k-btn color="red darken-4" class="mx-2 k-tight-btn k-nocaps-btn" dark
					v-if="worker_status == 'processing'"
					@click="cancel_worker_job"
				><k-icon small class="mr-2">fas fa-pause</k-icon> Pause Worker Job</k-btn>
			</div>

		</div>

		<!-- action buttons on the job: show if we have a cram_job_id -->
		<div class="mt-5 d-flex justify-center" 
			style="position:relative"
			v-if="job_status"
		>
			<k-btn color="primary" class="mx-2"
				v-if="save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-save</k-icon> Save Job</k-btn>

			<k-btn color="green darken-3" dark class="mx-2 elevation-0"
				v-if="!save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-circle-check</k-icon> Job Saved</k-btn>

			<k-btn color="primary" class="mx-2"
				v-if="worker_needs_to_run"
				:disabled="worker_status_includes(['queued', 'processing'])"
				@click="run_worker_job"
			><k-icon smallX class="mr-2">fas fa-person-running</k-icon> Run SCRAM Worker Job</k-btn>

			<k-btn color="primary" class="mx-2"
				:disabled="!grid_view_available"
				@click="view_grid"
			><k-icon smallX class="mr-2">fas fa-grid</k-icon> Grid View</k-btn>

			<k-btn color="primary" class="mx-2"
				:disabled="!grid_view_available"
				@click="launch_alignment_report"
			><k-icon smallX class="mr-2">fas fa-list-radio</k-icon> Report</k-btn>

			<!-- Hidden btn to call evaluate_params; this should be done automatically when needed -->
			<k-btn small style="opacity:0; position:absolute; right:0; top:0;"
				@click="evaluate_params"
			>EP</k-btn>
		</div>

		<!-- Details of job status -->
		<div class="mt-5 py-2 d-flex justify-center grey lighten-3" style="border-radius:8px"
			v-if="job_status"
		>
			<div class="mx-2">
				Worker status: <b>{{worker_status}}</b>
			</div>
			<div class="mx-2" v-if="updated_at_display">
				Last updated: <b>{{updated_at_display}}</b>
			</div>
			<div class="mx-2" v-if="job_status.job_error">
				JOB ERROR: <b>{{job_status.job_error}}</b>
			</div>
			<div class="mx-2" v-if="job_status.token_cost>0">
				Token cost: <b>${{job_status.token_cost.toFixed(4)}}</b>
			</div>
			<div class="mx-2" v-if="job_status.genai_time_spent>0">
				GenAI duration: <b>{{time_spent_display}}</b>
			</div>
		</div>

	</k-card-text>
</k-card>
</k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import FrameworkSwitcher2 from '../frameworks/FrameworkSwitcher2'
import SCRAMReportMixin from './SCRAMReportMixin.js'
import SCRAMJobStats from './SCRAMJobStats'

export default {
	mixins: [SCRAMReportMixin],
	components: { FrameworkSwitcher2, SCRAMJobStats },
	props: {
		// params: object, must include at least cram_job_id, which can be 0 if we're creating a new job
		params: { required: true },
	},
	data() { return {
		dialog_open: true,
		initialized: false,
		show_empty_report_text_interface: false,
		show_universe_details_toggle: true,
		show_identifier_text_fields: false,
		job_type_switch_complete: true,

		payload: null,
		// matrix: null,

		cwj_timeout_duration: 500,

		cram_job_id: 0,
		cram_job_title: '',
		report_text: '',
		job_type: '',
		// job_status: null,

	}},
	computed: {
		...mapState(['site_config', 'framework_records', 'user_info']),
		...mapGetters([]),

		report_title() {
			let s = `<b>Rosetta Align:</b> `
			if (this.cram_job_id > 0) s += `SCRAM Job ID ${this.cram_job_id}`
			else s += 'New SCRAM Job'
			return s
		},

		universes_specified() {
			// check for a resource framework identifier
			if (!this.ru.frameworkIdentifier) return false
			// check for at least one competency identifier
			if (!this.competency_universe_items[0]?.frameworkIdentifier) return false
			// if we have both, true
			return true
		},

		show_universe_details() {
			// until the universes are specified, always show them
			if (!this.universes_specified) return true

			// once they're specified, allow the user to toggle
			return this.show_universe_details_toggle
		},

		resource_framework_switcher_btn_content() {
			return `<i class="fas fa-cubes mr-2""></i>${this.ru.frameworkIdentifier ? 'Change' : 'Choose'} ${this.ru_label} Framework…`
		},

		comp_framework_switcher_btn_content() {
			return `<i class="fas fa-map mr-2""></i>Choose ${this.cu_label} Framework…`
		},

		resource_framework_switcher_framework_records() {
			// for crosswalks, we use the same options in both universe switchers
			if (this.crosswalk_job) return this.comp_framework_switcher_framework_records

			return this.framework_records.filter(x=>{
				return x.json.CFDocument.frameworkType == 'Resource Collection'
			})
		},

		comp_framework_switcher_framework_records() {
			return this.framework_records.filter(x=>{
				return x.json.CFDocument.frameworkType != 'Resource Collection'
			})
		},

		worker_status() { 
			return this.job_status?.worker_status || 'unknown'
		},

		ws_stats() {
			return this.job_status?.worker_status_stats ?? {}
		},

		matrix_worker_needs_to_run() {
			return this.matrix_stats.missingGenAICount > 0
		},

		crosswalk_worker_needs_to_run() {
			return this.matrix_stats.missingCrosswalkCount > 0
		},

		worker_needs_to_run() {
			return this.matrix_worker_needs_to_run || this.crosswalk_worker_needs_to_run
		},

		updated_at_display() {
			if (empty(this.job_status?.updated_at)) return ''
			return date.format(U.convert_to_local_date(this.job_status.updated_at), 'M/D/YY h:mm A')
		},

		time_spent_display() {
			if (empty(this.job_status)) return ''
			// first convert genai_time_spent from ms to seconds
			let sec = Math.round(this.job_status.genai_time_spent / 1000)
			if (sec < 1) {
				return '< 1 sec'
			}

			// separate into number of minutes and seconds, then format rv
			let min = Math.floor(sec / 60)
			sec = (sec % 60)
			if (sec*1 < 10) sec = '0' + sec
			return `${min}:${sec}`
		},

		cells_processed() {
			if (empty(this.ws_stats) || empty(this.matrix_stats)) return 0
			return this.matrix_stats.matrixCellCount - this.matrix_stats.missingGenAICount
		},

		cells_processed_pct() {
			if (empty(this.ws_stats) || empty(this.matrix_stats)) return 0
			if (this.matrix_stats.matrixCellCount == 0) return 0
			return Math.floor(this.cells_processed / this.matrix_stats.matrixCellCount * 100)
		},

		crosswalks_processed() {
			if (empty(this.ws_stats) || empty(this.matrix_stats)) return 0
			return this.matrix_stats.crosswalkCount - this.matrix_stats.missingCrosswalkCount
		},

		crosswalks_processed_pct() {
			if (empty(this.ws_stats) || empty(this.matrix_stats)) return 0
			if (this.matrix_stats.crosswalkCount == 0) return 0
			return Math.floor(this.crosswalks_processed / this.matrix_stats.crosswalkCount * 100)
		},

		// shortcuts for competency/resource universes
		// note that a single job can include competencies from multiple comp frameworks,
		// but can only include resources from a single resource framework
		// (the scram API can also be called by specifying an array of individual resources in resourceUniverse,
		//  but not via this interface)
		competency_universe_items() { return this.payload.competencyUniverse },
		ru() { return this.payload.resourceUniverse[0] },

		cu_fr() { 
			let arr = []
			for (let cu of this.competency_universe_items) {
				let fid = cu.frameworkIdentifier
				
				if (empty(fid)) {
					arr.push(null)
					continue
				}

				let fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
				if (empty(fr)) {
					// we apparently don't know anything about this framework
					arr.push(null)
					continue
				}

				// load the framework if necessary
				U.load_framework_and_cfo(fid, {show_loader: true}).then(fr=>{
					// do something when framework is loaded?
				})
				arr.push(fr)
			}
			return arr
		},
		
		ru_fr() { 
			let fid = this.ru.frameworkIdentifier
			
			if (empty(fid)) return null

			let fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
			if (empty(fr)) return null	// we apparently don't know anything about this framework
			// load the framework if necessary
			U.load_framework_and_cfo(fid, {show_loader: true}).then(fr=>{
				// do something when framework is loaded?
			})
			return fr
		},
		
		opened_from_embedded() {
			return this.params?.opened_from_embedded == true
		},	

		fullscreen() {
			return true
		},

		dialog_class() {
			let s = 'k-scram-job-dialog'

			if (this.opened_from_embedded) {
				s += ' k-scram-job-dialog-embedded'
			}

			return s
		},

		job_parameters_changed() {
			// if (empty(this.payload) || empty(this.job_status?.universe_params)) {
			// 	return true
			// }
			if (empty(this.payload) || empty(this.job_status)) {
				return true
			}

			// make sure params are in the right order for params and job_status.universe_params
			let saved_payload = JSON.stringify({
				resourceUniverse: this.job_status.universe_params?.resourceUniverse,
				competencyUniverse: this.job_status.universe_params?.competencyUniverse,
				resourceAggregationItemType: this.job_status.universe_params?.resourceAggregationItemType,
				educationLevelMatchTolerance: this.job_status.universe_params?.educationLevelMatchTolerance,
				cram_job_title: (this.job_status?.cram_job_title ?? ''),
				report_text: (this.job_status?.report_text ?? ''),
				job_type: (this.job_status?.job_type ?? ''),
			})
			// console.log('saved', saved_payload)

			// the incoming job_status.payload will have xUniverse params stringified, so we need to do the same here
			let service_payload = this.get_service_payload()
			let current_payload = JSON.stringify({
				resourceUniverse: service_payload.resourceUniverse,
				competencyUniverse: service_payload.competencyUniverse,
				resourceAggregationItemType: this.payload.resourceAggregationItemType,
				educationLevelMatchTolerance: this.payload.educationLevelMatchTolerance,
				cram_job_title: this.cram_job_title,
				report_text: this.report_text,
				job_type: this.job_type,
			})
			// console.log('current', current_payload)

			return !(saved_payload == current_payload)
		},

		save_job_available() {
			if (this.worker_status_includes(['queued', 'processing'])) return false
			if (!this.job_parameters_changed) return false
			return true
		},

		grid_view_available() {
			if (this.worker_status_includes(['queued', 'processing'])) return false
			if (this.worker_needs_to_run) return false
			if (this.save_job_available) return false
			return true
		},

	},
	watch: {
		params: { deep:true, immediate: true, handler() {
			// console.warn('PARAMS WATCHER')
		}},

		worker_status: { immediate: true, handler() {
			// console.warn('status changed to ' + this.worker_status)
		}},

		payload: { deep:true, immediate: false, handler() {
			// when payload changes, if we have valid resource and comp universes, evaluate params
			if (this.initialized) {
				if (!empty(this.payload.resourceUniverse[0].frameworkIdentifier)) {
					if (!empty(this.payload.competencyUniverse[0].frameworkIdentifier)) {
						// console.warn('payload changed with valid universes')
						this.evaluate_params()
					}
				}
			}
		}},

		job_type() {
			this.job_type_switch_complete = false
			setTimeout(x=>this.job_type_switch_complete = true)
		},
	},
	created() {
	},
	async mounted() {
		vapp.scram_job_component = this
		// console.warn('mounting SCRAMJobView: ' + this.params.cram_job_id)

		// get initial cram_job_id from params; could be 0
		this.cram_job_id = this.params.cram_job_id

		// if we have a cram_job_id we're starting with, get initial status
		// this will fill in the payload if universe_params are saved with the job
		if (this.cram_job_id > 0) {
			// also get the matrix if we have it
			await this.get_job_status('get_matrix')
		} else {
			// set the initial job_type to the value from params
			this.job_type = this.params.initial_job_type_for_new_job ?? 'align'
		}

		// if payload is still null, create default payload to use
		if (this.payload === null) {
			this.create_ui_payload()
			// and if we received an initial_resource_framework_id in the initial params, use it in the initial payload
			if (!empty(this.params.initial_resource_framework_id)) {
				this.payload.resourceUniverse[0].frameworkIdentifier = this.params.initial_resource_framework_id
			}
		}

		// if universes are specified, hide them by default
		if (this.universes_specified) this.show_universe_details_toggle = false

		this.initialized = true
	},
	beforeDestroy() {
		clearTimeout(this.cwj_timeout)
	},
	methods: {
		get_default_payload() {
			return {
				job_type: this.params.initial_job_type_for_new_job,	// 'align', 'crosswalk'
				resourceUniverse: [
					{
						frameworkIdentifier: '',
						itemIdentifiers: [],
						CFItemTypes: [],
						// note that the below are only used for crosswalk jobs
						alignToBranches: true,
						outcomesOnly: true,
						outcomeLeafsOnly: false,
						educationLevel: [],
					}
				],
				/* alt resourceUniverse: 
				resourceUniverse: [
					{
						resourceSourcedId: '',		// '12345'
						title: '',
						alignmentMetadata: [''],
						educationLevel: [],
					}, ...
				], ...
				*/
				competencyUniverse: [
					{
						frameworkIdentifier: '',
						itemIdentifiers: [],
						CFItemTypes: [],
						alignToBranches: true,
						outcomesOnly: true,
						outcomeLeafsOnly: false,
						educationLevel: [],
					}
				],
				matchCountToReturn: 10,
				returnedCFItemData: 'base',
				searchTerms: '',

				educationLevelMatchTolerance: -1,
				resourceAggregationItemType: '',	// Item type to evaluate via aggregation (e.g. chapters/sections)

				alignmentService: '',

				action: '',
			}
		},

		set_cu_item_types_for_ui(cu) {
			// for the UI, we combine CFItemTypes, outcomesOnly, and outcomeLeafsOnly
			if (cu.CFItemTypes.length == 0) {
				if (cu.outcomesOnly) cu.CFItemTypes = ['all_outcomes']
				else if (cu.outcomeLeafsOnly) cu.CFItemTypes = ['all_outcome_leafs']
			}
		},

		create_ui_payload(received_payload) {
			if (empty(received_payload)) {
				received_payload = {}
				// DEBUG: use demo payload...
				// received_payload = this.get_demo_payload7()
			}
			// make sure received_payload is "pure" (not reactive)
			received_payload = sc(received_payload)

			// start with default_payload; fill in with any values from received_payload
			// note that we might need to do something more sophisticated in the future if we fiddle with the format of the universe definitions...
			let p = this.get_default_payload()
			for (let key in received_payload) {
				// the xxYniverse fields come in json-encoded
				if (key == 'resourceUniverse' || key == 'competencyUniverse') {
					p[key] = JSON.parse(received_payload[key])
				} else {
					p[key] = received_payload[key]
				}
			}

			this.set_cu_item_types_for_ui(p.competencyUniverse[0])

			this.payload = p
		},

		get_service_payload() {
			let payload = {}

			if (this.payload != null) {
				payload = sc(this.payload)

				// reverse CFItemTypes translation above
				let cu = payload.competencyUniverse[0]
				if (cu.CFItemTypes[0] == 'all_outcomes') {
					cu.CFItemTypes = []
					cu.outcomesOnly = true
					cu.outcomeLeafsOnly = false
				} else if (cu.CFItemTypes[0] == 'all_outcome_leafs') {
					cu.CFItemTypes = []
					cu.outcomesOnly = false
					cu.outcomeLeafsOnly = true
				} else {
					cu.outcomesOnly = false
					cu.outcomeLeafsOnly = false
				}
				
				// stringify universes
				payload.resourceUniverse = JSON.stringify(payload.resourceUniverse)
				payload.competencyUniverse = JSON.stringify(payload.competencyUniverse)
			}

			// add user_id
			payload.user_id = this.user_info.user_id

			// add cram_job_title, report_text, and job_type
			payload.cram_job_title = this.cram_job_title?.trim() || '*CLEAR*'
			payload.report_text = this.report_text?.trim() || '*CLEAR*'
			payload.job_type = this.job_type

			// add cram_job_id if we have it
			if (!empty(this.cram_job_id)) {
				payload.cram_job_id = this.cram_job_id
			}

			return payload
		},

		////////////////////////////////
		async set_resource_framework(selected_framework_identifier) {
			// TODO?: if the RU wasn't empty make sure the user really wants to change this, or remember previous settings
			let dru = this.get_default_payload().resourceUniverse[0]
			for (let key in dru) {
				if (key == 'frameworkIdentifier') {
					this.ru.frameworkIdentifier = selected_framework_identifier
				} else {
					this.ru[key] = dru[key]
				}
			}

			// load the framework if necessary
			let fr = this.framework_records.find(x=>x.lsdoc_identifier == selected_framework_identifier)
			if (empty(fr)) return	// this shouldn't ever happen; we just selected the framework, so we know we know about it
			await U.load_framework_and_cfo(selected_framework_identifier, {show_loader: true})
		},

		async set_comp_framework(cui, selected_framework_identifier) {
			let cu = this.competency_universe_items[cui]

			// TODO?: if the CU wasn't empty make sure the user really wants to change this, or remember previous settings
			let dcu = this.get_default_payload().competencyUniverse[0]
			for (let key in dcu) {
				if (key == 'frameworkIdentifier') {
					cu.frameworkIdentifier = selected_framework_identifier
				} else {
					cu[key] = dcu[key]
				}
			}
			this.set_cu_item_types_for_ui(cu)

			// load the framework if necessary
			let fr = this.framework_records.find(x=>x.lsdoc_identifier == selected_framework_identifier)
			if (empty(fr)) return	// this shouldn't ever happen; we just selected the framework, so we know we know about it
			await U.load_framework_and_cfo(selected_framework_identifier, {show_loader: true})
		},

		async choose_branches(framework_identifier, current_branches) {
			let data = {
				framework_identifier: framework_identifier,
				selected_items: current_branches
				// allow_framework_switching: false // don't allow framework switching here
			}

			let show_data = {
				// fn called when embedded satchel is hidden
				embed_hide_callback_fn: arg => {
					// // console.warn('embed_hide_callback_fn', arg)
				},

				// fn called continuously while embedded satchel is open; if it returns true, embedded satchel will be closed
				hide_fn: () => {
					return vapp.scram_job_view_params == false
				},

				// small_frame_top: 60,
				// small_frame_width: this.satchel_embed_width
			}

			await vapp.$refs.satchel.execute('show', show_data)
			await vapp.$refs.satchel.execute('load_framework', data)
			vapp.$refs.satchel.execute('chooser', { chooser_mode: true }).then((chosen_branch) => {
				// console.log(chosen_branch)
				// if user previously chose this item, remove it
				let i = current_branches.findIndex(identifier=>identifier==chosen_branch.cfitem.identifier)
				if (i > -1) {
					current_branches.splice(i, 1)

				} else {
					// else add it
					current_branches.push(chosen_branch.cfitem.identifier)
				}

				// re-initialize the chooser to show the updated choices
				this.choose_branches(framework_identifier, current_branches)
			})
		},

		choose_resource_branches() {
			this.choose_branches(this.ru.frameworkIdentifier, this.ru.itemIdentifiers)
		},

		choose_comp_branches(cui) {
			let cu = this.competency_universe_items[cui]
			this.choose_branches(cu.frameworkIdentifier, cu.itemIdentifiers)
		},

		////////////////////////////////
		framework_title_display(fr) {
			if (empty(fr)) return 'Framework Title'
			return fr.json.CFDocument.title
		},

		branch_title_display(fr, ids) {
			if (empty(fr)) return 'Branch Title(s)'
			let ss = ''
			if (ids.length > 1) ss = '<ul>'

			for (let id of ids) {
				let cfi = fr?.cfo?.cfitems[id]
				let s
				if (empty(cfi)) {
					s = '<i>UNKNOWN BRANCH</i>'
				} else {
					s = cfi.fullStatement
					if (cfi.humanCodingScheme) s = `<b>${cfi.humanCodingScheme}</b> ${s}`
				}
				if (ids.length > 1) {
					ss += `<li>${s}</li>`
					// ss += `<div style="margin-left:20px; text-indent:-20px">${s}</div>`
				} else {
					ss += s
				}
			}
			return ss
		},

		item_type_list(fr) {
			if (empty(fr?.cfo?.item_types)) return []

			let arr = []
			for (let t of fr.cfo.item_types) {
				arr.push({ value: t, text: t })
			}
			return arr
		},

		aggregation_item_type_list() {
			let arr = this.item_type_list(this.ru_fr)
			arr.unshift({ value: '', text: 'NONE (no aggregation)' })
			return arr
		},

		cu_item_type_list(fr) {
			let arr = this.item_type_list(fr)
			arr.unshift({ value: 'all_outcome_leafs', text: 'ALL “OUTCOME LEAFS”' })
			arr.unshift({ value: 'all_outcomes', text: 'ALL “OUTCOMES”' })
			return arr
		},

		cu_item_types_changed(cu) {
			// if we have two or more items selected...
			if (cu.CFItemTypes.length >= 2) {
				// if the user just chose 'all_outcome_leafs' or 'all_outcomes', just choose that option
				if (cu.CFItemTypes[cu.CFItemTypes.length-1] == 'all_outcome_leafs') {
					cu.CFItemTypes = ['all_outcome_leafs']
				} else if (cu.CFItemTypes[cu.CFItemTypes.length-1] == 'all_outcomes') {
					cu.CFItemTypes = ['all_outcomes']

				// else if the first item is one of these, shift it off the list
				} else if (cu.CFItemTypes[0] == 'all_outcome_leafs' || cu.CFItemTypes[0] == 'all_outcomes') {
					cu.CFItemTypes.shift()
				}
			}
		},

		education_level_list() {
			let arr = []
			for (let i = 1; i < this.$store.state.grades.length; ++i) {
				arr.push({
					value: this.$store.state.grades[i].value,
					text: this.$store.state.grades[i].text,
				})
			}
			return arr
		},

		education_level_tolerance_list() {
			let arr = [
				{ value:-1, text: 'Ignore EL when considering matches' },
				{ value:0, text: 'Consider exact EL matches only' },
				{ value:1, text: 'Allow for EL mismatches of ± 1 grade level' },
				{ value:2, text: 'Allow for EL mismatches of ± 2 grade levels' },
				{ value:3, text: 'Allow for EL mismatches of ± 3 grade levels' },
			]
			return arr
		},

		////////////////////////////////////////////////////////////////
		async execute_job(action) {
			let payload = this.get_service_payload()
			payload.action = action

			// if (action != 'get_job_status') U.loading_start()
			let result = await U.ajax_promise('align/scram', payload)
			// U.loading_stop()

			// if we were trying to get the matrix and that failed, we should be able to proceed
			if (result.status == 'matrix_incomplete') {
				console.warn('matrix_incomplete')

			} else if (result.status != 'ok') {
				this.$alert('ERROR: ' + result.status)
				return
			}

			// console.log(`execute_job (${action}) / NOW ${result.job_status.worker_status} == WAS ${this.worker_status}`)

			// for some actions, return here
			if (['clone_job', 'delete_job'].includes(action)) return result

			// if status changed from 'processing' or 'cancel_requested' to something else, immediately run evaluate_params
			if (this.worker_status_includes(['processing', 'cancel_requested']) && result.job_status.worker_status != this.worker_status) {
				this.$nextTick(x=>this.evaluate_params());
			}

			// we will always get back job_status, but if we haven't yet saved the job, it won't include a cram_job_id
			this.job_status = result.job_status

			// if we do get a cram_job_id...
			if (!empty(this.job_status.cram_job_id)) {
				// set cram_job_id if we just got it
				if (this.job_status.cram_job_id != this.cram_job_id) {
					// console.warn('setting cram_job_id to ' + this.cram_job_id)
					this.cram_job_id = this.job_status.cram_job_id
				}

				// if this.payload is null and we received universe_params, create payload
				if (this.payload === null && !empty(this.job_status.universe_params)) {
					this.create_ui_payload(this.job_status.universe_params)
				}
			}

			// and if we get a matrix, save it
			if (!empty(result.matrix)) {
				this.matrix = result.matrix
			}

			return result
		},

		async evaluate_params() {
			await this.execute_job('evaluate_params')
		},

		async run_worker_job() {
			// start the job running, then immediately start checking
			await this.execute_job('run_worker_job')
			this.$nextTick(x=>this.get_job_status())
		},

		async get_job_status(flag = '') {
			clearTimeout(this.cwj_timeout)

			let previous_worker_status = this.worker_status

			// if flag is 'get_matrix', call get_matrix instead of get_job_status; this way we *also* get the matrix
			if (flag == 'get_matrix') {
				await this.execute_job('get_matrix')
			} else {
				await this.execute_job('get_job_status')
			}

			// set incoming cram_job_title and report_text
			this.cram_job_title = this.job_status?.cram_job_title ?? ''
			this.report_text = this.job_status?.report_text ?? ''
			this.job_type = this.job_status?.job_type ?? 'align'

			// if worker is running, automatically re-check again after a delay
			if (this.worker_status_includes(['queued', 'processing', 'cancel_requested'])) {
				this.cwj_timeout = setTimeout(x=>{
					// console.log('auto-get_job_status / last_status: ' + this.worker_status)
					this.get_job_status()
				}, this.cwj_timeout_duration)

			} else {
				// else if we just finished processing, get the matrix (unless we just got the matrix)
				if (flag != 'get_matrix') {
					if (previous_worker_status != this.worker_status) {
						if (['queued', 'processing', 'cancel_requested'].includes(previous_worker_status)) {
							await this.execute_job('get_matrix')
						}
					}
				}
			}
		},

		async cancel_worker_job() {
			await this.execute_job('cancel_worker_job')
		},

		////////////////////////////////////////////////////////////////
		worker_status_includes(arr) {
			if (typeof(arr) == 'string') arr = [arr]
			return arr.includes(this.worker_status)
		},

		async save_job() {
			U.loading_start()
			await this.execute_job('save_job')
			U.loading_stop()
		},

		async delete_job() {
			// if the job hasn't been saved, we just close the interface
			if (this.cram_job_id != 0) {
				let val = await U.confirm_promise({
					title: 'Are you sure?',
					text: 'Are you sure you want to delete this job? (Note that resource and alignment data will <i>not</i> be deleted; only the job metadata will be deleted.)',
					acceptText: 'Delete Job',
					acceptColor: 'red darken-3',
					acceptIcon: 'fas fa-trash-alt',
				})
				if (!val) return
				// user confirmed

				U.loading_start()
				let rv = await this.execute_job('delete_job')
				U.loading_stop()

				// if there was an error, just return (execute_job should have shown/handled the error)
				if (empty(rv)) return
			}

			// close this interface; the job table should then appear with this job removed
			this.close_interface()
		},

		async clone_job() {
			let val = await U.prompt_promise({
				title: 'Clone Job',
				text: 'Enter a title for the cloned job. After cloning, you can update the Resource Universe and/or Competency Universe for the new job.',
				initialValue: this.cram_job_title,
				disableForEmptyValue: true,
				acceptText: 'Proceed',
				acceptIcon: 'fas fa-circle-arrow-right',
				dialogMaxWidth: 600,
			})
			// empty string is allowed here; if the user cancels, val will === false
			if (val === false) return
			val = val.trim()

			// user confirmed; set cram_job_title before passing through to clone_job
			this.cram_job_title = val

			U.loading_start()
			let rv = await this.execute_job('clone_job')
			U.loading_stop()

			// if there was an error, just return (execute_job should have shown/handled the error)
			if (empty(rv)) return

			// update vapp.scram_manager_update_id to force the SCRAMManager to reload jobs
			vapp.scram_manager_update_id += 1

			// then switch to the new job
			this.$emit('switch_job', rv.job_status.cram_job_id)
		},

		async view_grid($event) {
			// if user holds shift key down while clicking the GRID VIEW btn, open in a new window
			if ($event?.shiftKey) {
				window.open(`/crm/${this.cram_job_id}/grid`)
				return
			}

			vapp.show_resource_matrix_view({
				cram_job_id: this.cram_job_id+'',
				dim: 'grid',
			})
		},

		async edit_resources() {
			if (!this.matrix) return
			for (let comp_id in this.matrix) {
				for (let resource_id in this.matrix[comp_id]) {
					vapp.show_resource_matrix_view({
						cram_job_id: this.cram_job_id+'',
						dim: 'resource',
						dim_id: resource_id,
						edit_at_start: true,
					})
					return
				}
			}
		},

		async edit_comps() {
			if (!this.matrix) return
			for (let comp_id in this.matrix) {
				vapp.show_resource_matrix_view({
					cram_job_id: this.cram_job_id+'',
					dim: 'comp',
					dim_id: comp_id,
					edit_at_start: true,
				})
				return
			}
		},

		async launch_alignment_report() {
			let url = `/crm/${this.cram_job_id}/ar`
			window.open(url, '_blank')
		},

		////////////////////////////////////////////////////////////////
		close_interface() {
			if (false) {
				vapp.$refs.satchel.execute('hide')
			}

			// update vapp.scram_manager_update_id to force the SCRAMManager to reload jobs
			vapp.scram_manager_update_id += 1
			this.$emit('dialog_cancel')
		},
	}
}
</script>

<style lang="scss">
.k-scram-job-dialog {
	.v-card {
		background-color:#fff;
		border-radius:0!important;
		// border:$v-blue-grey-darken-4 4px solid;
	}

	.v-card__text {
		padding-left:16px!important;
		padding-right:16px!important;
		// padding-right:496px!important;
	}

	h3 {
		font-size:18px;
	}

	.v-select__selections {
		// padding:4px 0 4px 0!important;

		.v-select__selection {
			font-size:14px;
			line-height:16px;
			margin-top:0px!important;
			margin-bottom:0px!important;
			padding:0!important;
		}
		.v-select__selection--comma {
			// margin-top:4px!important;
		}

		input {
			padding:0;
			margin:0;
		}
	}
}

.k-scram-job-dialog-embedded {
	.v-card__text {
		padding-left:16px!important;
		// padding-right:16px!important;
		padding-right:472px!important;
	}
}

.k-scram-job-dialog-fullscreen {
	.v-card {
		border-radius:0!important;
	}

	.v-card__text {
		padding-left:16px!important;
		// padding-right:16px!important;
		padding-right:472px!important;
	}
}

.k-scram-job-framework-title, .k-scram-job-branch-title {
	border:1px solid #666;
	padding:4px 8px;
	font-size:14px;
	line-height:16px;
	border-radius:4px;
	max-width:364px;
}

.k-scram-job-framework-title {
	background-color:#eee;
}

.k-scram-job-branch-title {
	background-color:#ccc;
	ul {
		padding-left:12px!important;
	}
	li {
		margin-top:4px;
	}
	li:first-of-type {
		margin-top:0;
	}
}

.k-scram-job-guid-field {
	font-size:10px;
	max-width:160px;
	input {
		font-family:monospace;
	}
}

.k-scram-job-status {
	font-size:18px;
	line-height:21px;
}

.k-rcram-job-type-indicator {
	background-color:#ccc;
	border-radius:8px;
	padding:8px 12px;
	font-size:18px;
	font-weight:bold;
}
</style>
